// /opt/isp_erp/frontend/src/components/tickets/RescheduleTicketDialog.jsx
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Box,
  Typography,
  CircularProgress
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';
import axios from '../../api/axios';

export default function RescheduleTicketDialog({ open, onClose, ticket, onSuccess }) {
  const [nuevaFecha, setNuevaFecha] = useState(null);
  const [motivo, setMotivo] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [verificando, setVerificando] = useState(false);

  const handleFechaChange = async (newDate) => {
    setNuevaFecha(newDate);
    setError('');
    
    if (newDate && ticket?.assigned_technician_id) {
      setVerificando(true);
      try {
        const fechaStr = newDate.toISOString().split('T')[0];
        const response = await axios.get(`/tickets/tecnicos-disponibles-por-fecha?fecha=${fechaStr}`);
        const disponible = response.data.some(t => t.id === ticket.assigned_technician_id);
        if (!disponible) {
          setError('⚠️ El técnico no está disponible en esta fecha (tiene permiso)');
        }
      } catch (err) {
        console.error('Error verificando disponibilidad:', err);
      } finally {
        setVerificando(false);
      }
    }
  };

  const handleSubmit = async () => {
    if (!nuevaFecha) {
      setError('Debe seleccionar una nueva fecha');
      return;
    }
    if (!motivo || motivo.trim() === '') {
      setError('Debe ingresar un motivo');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await axios.put(`/tickets/${ticket.id}/reagendar`, null, {
        params: {
          nueva_fecha: nuevaFecha.toISOString(),
          motivo: motivo
        }
      });

      onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al reagendar ticket');
    } finally {
      setLoading(false);
    }
  };

  const fechaActual = ticket?.arrival_time || ticket?.scheduled_date;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Reagendar Ticket
        <Typography variant="body2" color="text.secondary">
          Ticket: {ticket?.ticket_number} - {ticket?.title}
        </Typography>
        {fechaActual && (
          <Typography variant="caption" color="text.secondary" display="block">
            Fecha actual: {new Date(fechaActual).toLocaleString()}
          </Typography>
        )}
        {ticket?.assigned_technician_name && (
          <Typography variant="caption" color="text.secondary" display="block">
            Técnico: {ticket.assigned_technician_name}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {error && (
            <Alert severity={error.includes('no está disponible') ? 'warning' : 'error'} sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
            <DateTimePicker
              label="Nueva fecha y hora"
              value={nuevaFecha}
              onChange={handleFechaChange}
              slotProps={{
                textField: {
                  fullWidth: true,
                  sx: { mb: 2 }
                }
              }}
              minDateTime={new Date()}
            />
          </LocalizationProvider>

          {verificando && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <CircularProgress size={16} />
              <Typography variant="caption">Verificando disponibilidad del técnico...</Typography>
            </Box>
          )}

          <TextField
            fullWidth
            label="Motivo del reagendamiento"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            multiline
            rows={3}
            placeholder="Ej: Cliente no disponible, Cambio de prioridad, Requiere materiales, etc."
            required
          />

          {nuevaFecha && !error && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <strong>Confirmar:</strong> El ticket será reagendado para el{' '}
              {nuevaFecha.toLocaleString()}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading || !nuevaFecha || !motivo || !!error}
        >
          {loading ? <CircularProgress size={24} /> : 'Reagendar Ticket'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
